/**
 * AeriosIQ brand assets — vector data verbatim from the designer file
 * `ARIOS2.1.svg` (delivered 2026-07-08, archived in the orchestration repo
 * under .vibe/_TRANSIST_FROM_WP_TO_AERIOSIQ/). No hand-tracing.
 *
 * The lockup is deliberately split into independently swappable parts
 * (transition doc 05 SS2): Mark (gradient network shape), Wordmark
 * ("Aerios" letterforms with dotless-i rect, currentColor), Badge
 * (gradient "IQ", Q as magnifier). Swap any part by editing its file only
 * — the composite AeriosIQLogo re-uses the exported path constants at the
 * designer's original coordinates, so spacing never drifts.
 *
 * Gradient (authoritative): #00829F at 0% -> #00303F at 77%.
 * Dark-surface treatment: deferred to the CI pass — the gradient is tuned
 * for light chrome; wordmark letters follow currentColor.
 */
import type { SVGProps } from "react";
import { useTheme } from "@mui/material/styles";

/**
 * Theme-aware fill (Christian, 2026-07-08): dark theme renders the ENTIRE
 * logo monochrome white (gradient's petrol end sinks into dark chrome);
 * light theme keeps the colored gradient. `variant` overrides the
 * auto-detection when a surface needs a fixed treatment.
 */
type LogoVariant = "auto" | "color" | "mono";

export const AERIOSIQ_MARK_PATH =
  "M2.51,3.3C6.32-.83,12.77-.92,16.71,3.02c1.9,1.9,2.86,4.37,2.88,6.86.05,5.22,2.16,10.2,5.85,13.89l.66.66c3.69,3.69,8.68,5.79,13.89,5.85,2.49.03,4.96.99,6.86,2.88,3.94,3.94,3.84,10.39-.29,14.2-3.61,3.33-9.72,3.34-13.33.02-2.07-1.9-3.13-4.46-3.19-7.04-.12-5.25-2.11-10.28-5.82-13.99l-.71-.71c-3.71-3.71-8.74-5.71-13.99-5.82-2.58-.06-5.14-1.12-7.04-3.19-3.32-3.62-3.31-9.73.02-13.33ZM16.74,33.13c-3.83-3.83-10.04-3.83-13.86,0-3.83,3.83-3.83,10.04,0,13.86,3.83,3.83,10.04,3.83,13.86,0,3.83-3.83,3.83-10.04,0-13.86ZM46.87,3c-3.83-3.83-10.04-3.83-13.86,0-3.83,3.83-3.83,10.04,0,13.86,3.83,3.83,10.04,3.83,13.86,0,3.83-3.83,3.83-10.04,0-13.86Z";

/** Mark only — favicon + collapsed-sidebar variant. */
export function AeriosIQMark({
  variant = "auto",
  ...props
}: SVGProps<SVGSVGElement> & { variant?: LogoVariant }) {
  const mode = useTheme().palette.mode;
  const mono = variant === "mono" || (variant === "auto" && mode === "dark");
  return (
    <svg
      // eslint-disable-next-line no-restricted-syntax -- SVG namespace URI is canonical http://; not an OWASP V9.1.1 concern.
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 49.74 49.96"
      role="img"
      aria-label="AeriosIQ"
      height={28}
      {...props}
    >
      <defs>
        <linearGradient
          id="aiq-grad-mark"
          x1="0"
          y1="25"
          x2="49.74"
          y2="25"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#00829f" />
          <stop offset=".77" stopColor="#00303f" />
        </linearGradient>
      </defs>
      <path
        fill={mono ? "currentColor" : "url(#aiq-grad-mark)"}
        d={AERIOSIQ_MARK_PATH}
      />
    </svg>
  );
}
